// src/pages/AccessMatrix.jsx
import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import "./access-matrix.css";

/* ===== Ruoli fallback (se da BE arrivano numeri) ===== */
const ROLE_FALLBACK = {
  1: { slug: "admin",        label: "Amministratore" },
  2: { slug: "centralino",   label: "Centralino" },
  3: { slug: "formatore",    label: "Formatore" },
  4: { slug: "rilevatore",   label: "Rilevatore" },
  5: { slug: "senior",       label: "Senior" },
  6: { slug: "senior_posa",  label: "Senior Posa" },
  7: { slug: "responsabile", label: "Responsabile" },
  8: { slug: "ospite",       label: "Ospite" },
};

const API_BASE = "/preventivi/admin";
const ZONE_PARAM = "zone_id";

/* ========================= Fetch helper ========================= */
const fetchJSON = async (u, opt = {}) => {
  const r = await fetch(u, {
    credentials: "same-origin",
    headers: { Accept: "application/json", ...(opt.headers || {}) },
    ...opt,
  });
  const ct = r.headers.get("content-type") || "";
  const txt = await r.text();
  let j = {};
  if (ct.includes("application/json")) {
    try { j = txt ? JSON.parse(txt) : {}; } catch { j = {}; }
  }
  if (!r.ok || j.status === "ERROR") {
    const msg = (j && j.message) ? j.message : (txt || `HTTP ${r.status}`);
    throw new Error(msg);
  }
  return j && Object.keys(j).length ? j : {};
};

/* ========================= Grouping helper ========================= */
const capGroup = (code) => {
  if (!code) return "Altro";
  if (code.startsWith("agenda.")) return "Agenda";
  if (code.startsWith("clienti.")) return "Clienti";
  if (code.startsWith("posa.")) return "Posa";
  if (code.startsWith("gestione.")) return "Gestione"; // ⬅️ aggiungi questa
  if (code.endsWith(".admin")) return "Amministrazione";
  if (["readOnly","restrictAssignedOnly","phoneDelay"].includes(code)) return "Sicurezza";
  if (code.startsWith("can")) return "Azioni su appuntamento";
  return "Altro";
};

/* ================== Meta permessi (descrizioni) ================== */
/* Struttura:
   code: {
     label, summary, enables[], blocks[], ui[], api[], notes[]
   }
*/




/* ================== Meta permessi (descrizioni) ================== */
/* Struttura:
   code: {
     label, summary, enables[], blocks[], ui[], api[], notes[]
   }
   NB: questi sono solo meta FE (non impattano direttamente il BE),
   servono per la AccessMatrix (tooltip / pannello di destra).
*/
const CAP_META = {
  /* ======= AMMINISTRAZIONE / PANNELLO ADMIN ======= */
  "admin.panel": {
    label: "Accesso pannello ACL",
    summary: "Permette di aprire la pagina Access / ACL e vedere la matrice permessi.",
    enables: [
      "Mostra la voce di menu 'Access' nella navbar",
      "Carica la matrice permessi (ruoli × capability) dal server",
      "Permette di modificare i permessi da AccessMatrix (perm_save.php)"
    ],
    blocks: [
      "La voce di menu 'Access' non è visibile",
      "Non è possibile aprire la pagina di gestione permessi"
    ],
    ui: [
      "Tab 'Access' nella topbar",
      "Pagina AccessMatrix (ruoli × permessi)",
      "Badge / contatori permessi attivi"
    ],
    api: [
      "GET /preventivi/admin/perm_list.php",
      "POST /preventivi/admin/perm_save.php"
    ],
    notes: [
      "Non concede altri poteri: governa solo la visibilità del pannello permessi.",
      "È consigliato tenerlo solo ad admin (o massimo a 1–2 super-admin)."
    ]
  },

  "lists.write": {
    label: "Listini: scrittura",
    summary: "Gestisce listini base/parametrici e moltiplicatori (colori, accessori, ecc.).",
    enables: [
      "Crea / modifica / duplica voci di listino",
      "Modifica parametri di calcolo (maggiorazioni, sconti, coefficienti per colore/accessori)",
      "Salva modifiche dalla pagina Listino"
    ],
    blocks: [
      "I campi della pagina Listino diventano solo lettura",
      "I pulsanti 'Salva', 'Duplica', 'Elimina' sono disabilitati o nascosti"
    ],
    ui: [
      "Pagina Listino con controlli di editing attivi",
      "Bottoni 'Nuovo', 'Salva', 'Duplica', 'Elimina' visibili e cliccabili"
    ],
    api: [
      "GET /preventivi/admin/listino_get.php",
      "POST /preventivi/admin/listino_save.php",
      "DELETE /preventivi/admin/listino_delete.php"
    ],
    notes: [
      "Impattano direttamente i prezzi nel preventivatore e in R012.",
      "Da tenere solitamente a admin / formatore responsabile del listino."
    ]
  },

  "users.write": {
    label: "Utenti: scrittura",
    summary: "Crea, modifica e attiva/disattiva utenti, ruoli e assegnazioni di zona.",
    enables: [
      "Crea nuovi utenti dall'area Amministrazione",
      "Modifica ruolo, zone, nome, email e parametri anagrafici",
      "Disattiva/riattiva un utente",
      "Reset (o rigenera) password per un utente"
    ],
    blocks: [
      "I form Utenti diventano solo lettura",
      "I pulsanti 'Nuovo', 'Salva', 'Reset password' risultano disabilitati"
    ],
    ui: [
      "Tab 'Utenti' con form editabili",
      "Bottoni di salvataggio attivi sulla scheda utente"
    ],
    api: [
      "GET /preventivi/admin/user_get.php",
      "POST /preventivi/admin/user_save.php"
    ],
    notes: [
      "Alcune modifiche (es. cambio ruolo) richiedono un nuovo login per riflettersi nella sessione.",
      "Da valutare con attenzione se concederlo a centralino/formatore: spesso si preferisce solo admin."
    ]
  },

  /* =============== AGENDA (PRO) GENERALE =============== */
  "agenda.admin": {
    label: "Admin Agenda",
    summary: "Sblocca strumenti avanzati di amministrazione in AgendaPro.",
    enables: [
      "Strumenti di auto-assegnazione appuntamenti per giorno/zona",
      "Reset massivo di stati/assegnazioni (se implementato nel BE)",
      "Pannelli di debug e funzioni diagnostiche (es. ricalcolo colori UI)"
    ],
    blocks: [
      "Nessun accesso agli strumenti 'admin' in AgendaPro",
      "Auto-assegnazioni e reset massivi non disponibili"
    ],
    ui: [
      "Sezione 'Admin / Strumenti avanzati' nella sidebar di AgendaPro",
      "Pulsanti per auto-assegnare rilievi ai rilevatori/senior"
    ],
    api: [
      "POST /preventivi/agenda/api/auto_assign_day.php",
      "Eventuali altre API admin in /preventivi/agenda/api/"
    ],
    notes: [
      "Non sostituisce i permessi puntuali (move, assign, cancel…).",
      "Da tenere solo a pochi ruoli (admin / formatore)."
    ]
  },

  "agenda.assign": {
    label: "Agenda: Assegnazioni (Rilevatore/Senior)",
    summary: "Permette di assegnare o cambiare Rilevatore e Senior su un appuntamento.",
    enables: [
      "Select 'Rilevatore' attiva nella modale evento",
      "Select 'Senior' attiva nella modale evento",
      "Aggiornamento dei badge 'Ril:' e 'Sr:' sulla card in Agenda"
    ],
    blocks: [
      "I menu a tendina per Rilevatore e Senior risultano disabilitati",
      "Non è possibile cambiare l'assegnazione tramite UI"
    ],
    ui: [
      "Selettori Rilevatore / Senior nella modale AgendaPro",
      "Badge aggiornati su card e tooltip"
    ],
    api: [
      "POST /preventivi/agenda/api/update_event.php (rilevatore_id, senior_id)"
    ],
    notes: [
      "Richiede comunque che l’evento sia visibile (agenda.read) e che il ruolo non sia readOnly.",
      "Per limitare per zona si combina con restrictAssignedOnly / zone filter."
    ]
  },

  "agenda.read": {
    label: "Agenda: lettura",
    summary: "Permette di visualizzare gli appuntamenti in AgendaPro/Call/Posa e aprire i dettagli.",
    enables: [
      "Visualizza eventi in griglia calendario (FullCalendar)",
      "Apri modale evento in sola lettura",
      "Vedi tooltip con nome cliente, zone, stato, ecc."
    ],
    blocks: [
      "Griglia agenda vuota (nessun evento mostrato)",
      "Nessun accesso alla modale evento"
    ],
    ui: [
      "Eventi colorati sulla griglia AgendaPro/Call",
      "Tooltip al passaggio del mouse",
      "Lista eventi in modalità listview"
    ],
    api: [
      "GET /preventivi/agenda/api/list_events.php",
      "GET /preventivi/agenda/api/event_get.php (se presente)"
    ],
    notes: [
      "È il permesso base per tutto ciò che riguarda Agenda.",
      "Molte altre capability (move, assign, confirm…) lo presuppongono."
    ]
  },

  "agenda.write": {
    label: "Agenda: scrittura (orari)",
    summary: "Consente di cambiare giorno/orario/durata degli appuntamenti (drag & resize).",
    enables: [
      "Trascinare gli eventi su altri slot/ giorni",
      "Modificare la durata con drag sui bordi",
      "Cambiare fascia oraria (M/P/S/Sera) dalla modale"
    ],
    blocks: [
      "Impossibile trascinare o ridimensionare gli appuntamenti",
      "I campi ora/ durata nella modale diventano solo lettura"
    ],
    ui: [
      "Cursore 'move' sugli eventi",
      "Handle di resize agli estremi degli slot",
      "Campi tempo/durata editabili nella modale"
    ],
    api: [
      "POST /preventivi/agenda/api/update_event.php (start_dt, end_dt, fascia)",
      "Eventuale log interno (ui_last_action = 'move' / 'resize')"
    ],
    notes: [
      "Può essere 'schiacciata' da readOnly: se readOnly è attivo, l’utente non modifica anche se ha agenda.write.",
      "Da combinare con canMoveEvent per controllare in modo più granulare."
    ]
  },

  "agenda.past": {
    label: "Agenda: vedere il passato",
    summary: "Permette di vedere gli appuntamenti già passati (es. per storici e controlli).",
    enables: [
      "Griglia con eventi anche su giorni precedenti a oggi",
      "Ricerca/navigazione sul passato"
    ],
    blocks: [
      "La API può filtrare automaticamente gli eventi futuri o da oggi in poi",
      "Il ruolo vede solo appuntamenti futuri (o di oggi)"
    ],
    ui: [
      "Possibilità di scorrere il calendario all’indietro e vedere gli eventi",
      "Elenco storico in modalità 'list' in AgendaPro/Call"
    ],
    api: [
      "GET /preventivi/agenda/api/list_events.php (rimozione del filtro date server side)"
    ],
    notes: [
      "Molto utile per ruoli di controllo/analisi (formatore, responsabile, admin)."
    ]
  },

  /* ==================== CLIENTI ==================== */
  "clienti.admin": {
    label: "Clienti: amministrazione",
    summary: "Gestione avanzata delle anagrafiche clienti (merge, blocchi, protezioni).",
    enables: [
      "Unire duplicati cliente (merge)",
      "Sbloccare record protetti per modifica",
      "Applicare flag di protezione su alcuni clienti"
    ],
    blocks: [
      "Nessun comando di merge duplicati",
      "Impossibile modificare flag protetti su clienti"
    ],
    ui: [
      "Bottoni 'Unisci', 'Blocca', 'Sblocca' nelle schede cliente (se implementati)"
    ],
    api: [
      "POST /preventivi/api/client_merge.php (se presente)",
      "POST /preventivi/api/client_lock.php (o simili)"
    ],
    notes: [
      "Da tenere in genere a admin / responsabile."
    ]
  },

  "clienti.read": {
    label: "Clienti: lettura",
    summary: "Permette di vedere i dettagli anagrafici del cliente, legati agli appuntamenti.",
    enables: [
      "Vedi nome, indirizzo, comune, CAP associato all’evento",
      "Apri una scheda cliente in sola lettura"
    ],
    blocks: [
      "Anagrafica cliente non accessibile (solo info minime nella card)",
      "Link 'Scheda cliente' disabilitato"
    ],
    ui: [
      "Tooltip o panel cliente in modale evento",
      "Scheda Cliente (view) in area amministrazione"
    ],
    api: [
      "GET /preventivi/api/client_get.php?id=…"
    ],
    notes: [
      "In combinazione con phoneDelay, il numero può risultare parzialmente oscurato."
    ]
  },

  "clienti.write": {
    label: "Clienti: scrittura",
    summary: "Consente di modificare e creare anagrafiche clienti.",
    enables: [
      "Edit dei campi cliente dalla scheda",
      "Salvataggio delle modifiche cliente",
      "Creazione di un nuovo cliente svincolato dall’agenda (lead diretto)"
    ],
    blocks: [
      "I campi del cliente sono disabilitati (read-only)",
      "Bottoni 'Salva' e 'Nuovo cliente' non utilizzabili"
    ],
    ui: [
      "Form cliente con campi attivi",
      "Bottoni di salvataggio nella scheda cliente"
    ],
    api: [
      "POST /preventivi/api/client_save.php"
    ],
    notes: []
  },

  /* ==================== POSA ==================== */
  "posa.admin": {
    label: "Posa: amministrazione",
    summary: "Funzioni amministrative per Agenda Posa (config, debug, mass edits).",
    enables: [
      "Modifiche di configurazione Posa",
      "Strumenti di debug per agenda_posa (colorazioni, stati)",
      "Eventuali azioni mass-update su eventi Posa"
    ],
    blocks: [
      "Nessuna azione di amministrazione Posa",
      "Solo gestione 'operativa' degli appuntamenti di posa"
    ],
    ui: [
      "Sezione 'Admin Posa' in Agenda Posa (se presente)"
    ],
    api: [
      "POST /preventivi/agenda_posa/api/* (endpoint admin dedicati)"
    ],
    notes: [
      "Permesso di livello alto, normalmente solo per admin."
    ]
  },

  "posa.read": {
    label: "Agenda Posa: lettura",
    summary: "Vede gli appuntamenti di posa (o di consegna) in Agenda Posa.",
    enables: [
      "Visualizzazione griglia Posa (FullCalendar/Lista)",
      "Dettaglio di ogni evento Posa (modale)"
    ],
    blocks: [
      "Agenda Posa appare vuota",
      "Nessun accesso alla modale evento Posa"
    ],
    ui: [
      "Celle di Agenda Posa con card appuntamenti"
    ],
    api: [
      "GET /preventivi/agenda_posa/api/listP_events.php"
    ],
    notes: []
  },

  "posa.write": {
    label: "Agenda Posa: scrittura",
    summary: "Permette di cambiare stato/esito di posa e assegnare le squadre.",
    enables: [
      "Cambio stato Posa (es. 'In corso', 'Chiuso', 'Non chiuso')",
      "Assegnazione squadra / Senior Posa",
      "Modifica date/slot se gestito anche lato posa"
    ],
    blocks: [
      "Pulsanti stato/esito in Agenda Posa disattivati",
      "Select assegnazione squadra disabilitate"
    ],
    ui: [
      "Chip stato Posa cliccabili",
      "Select 'squadra', 'senior posa' attive"
    ],
    api: [
      "POST /preventivi/agenda_posa/api/updateP_event.php"
    ],
    notes: [
      "Si applica solo in Agenda Posa; non regola gli esiti Senior vendita."
    ]
  },

  /* ========== AZIONI PUNTUALI SU APPUNTAMENTO (Agenda) ========== */
  canMoveEvent: {
    label: "Sposta evento (drag/resize)",
    summary: "Consente di spostare gli appuntamenti in Agenda (giorno/ora/durata).",
    enables: [
      "Drag&drop appuntamento in AgendaPro/Call",
      "Modifica della durata tramite resize",
      "Cambio fascia oraria mantenendo slot coerente"
    ],
    blocks: [
      "Gli eventi non si possono trascinare",
      "I bordi di resize non funzionano"
    ],
    ui: [
      "Evento marcato come draggable/resizable in FullCalendar",
      "Handle visibili ai bordi del blocco"
    ],
    api: [
      "POST /preventivi/agenda/api/update_event.php (start_dt, end_dt, fascia, durata)"
    ],
    notes: [
      "Spesso usato insieme a agenda.write; se readOnly = 1, questa capability è ignorata lato FE."
    ]
  },

  canConfirm: {
    label: "Conferma appuntamento",
    summary: "Permette di marcare l'appuntamento come 'confermato' dal centralino / operatore.",
    enables: [
      "Bottone 'Conferma' nella modale AgendaPro/Call",
      "Cambio stato a 'confermato' (S1) e colore relativo"
    ],
    blocks: [
      "Nessun bottone 'Conferma' visibile",
      "Il ruolo non può modificare lo stato a 'confermato'"
    ],
    ui: [
      "Chip/bottone 'Conferma' nella modale evento",
      "Colorazione 'verde confermato' sulla card"
    ],
    api: [
      "POST /preventivi/agenda/api/update_event.php?action=CONFIRM"
    ],
    notes: [
      "Può triggerare eventuali notifiche (SMS, WhatsApp, email) se previste lato BE."
    ]
  },

  canAssignRilevatore: {
    label: "Assegna Rilevatore",
    summary: "Permette di scegliere o cambiare il rilevatore per un appuntamento.",
    enables: [
      "Select 'Rilevatore' attiva nella modale",
      "Aggiornamento del badge 'Ril:' sulla card Agenda"
    ],
    blocks: [
      "Select Rilevatore disabilitata per il ruolo",
      "Nessun cambio di rilevatore possibile da UI"
    ],
    ui: [
      "Menu a tendina Rilevatore nella modale",
      "Badge Ril: [nome] aggiornato nella card"
    ],
    api: [
      "POST /preventivi/agenda/api/update_event.php (rilevatore_id)"
    ],
    notes: []
  },

  canAssignSenior: {
    label: "Assegna Senior",
    summary: "Consente di assegnare il Senior (consulente) a un appuntamento.",
    enables: [
      "Select 'Senior' attiva nella modale",
      "Badge S: [nome senior] sulle card AgendaPro"
    ],
    blocks: [
      "Select Senior disabilitata",
      "Non si può più cambiare / assegnare Senior dal frontend"
    ],
    ui: [
      "Selettore Senior nella modale evento",
      "Badge 'S:' su card AgendaPro"
    ],
    api: [
      "POST /preventivi/agenda/api/update_event.php (senior_id, owner_user_id)"
    ],
    notes: [
      "Spesso dato solo a centralino, responsabile, admin o ruoli di pianificazione senior."
    ]
  },

  canCancel: {
    label: "Annulla appuntamento",
    summary: "Permette di segnare un appuntamento come 'annullato' (S7).",
    enables: [
      "Bottone 'Annulla' nella modale",
      "Impostazione stato a 'annullato', colore rosa/rosso"
    ],
    blocks: [
      "Nessun bottone 'Annulla' in modale",
      "La UI non può più settare lo stato annullato"
    ],
    ui: [
      "Chip 'Annulla' visibile",
      "Colorazione S7 (annullato) sull'evento in Agenda"
    ],
    api: [
      "POST /preventivi/agenda/api/update_event.php?action=CANCEL"
    ],
    notes: [
      "Può richiedere conferma con motivo di annullamento."
    ]
  },

  canReset: {
    label: "Reset stato (soft)",
    summary: "Riporta l’evento a uno stato iniziale 'pulito' (logica definita da BE).",
    enables: [
      "Bottone 'Reset stato' nella UI",
      "Reset parziale di esiti/lock e stato UI"
    ],
    blocks: [
      "Niente reset rapido; occorre intervenire manualmente dal BE"
    ],
    ui: [
      "Pulsante 'Reset' nella modale se implementato",
      "Colori/stati riportati a un default (es. prenotato)"
    ],
    api: [
      "POST /preventivi/agenda/api/update_event.php?action=RESET"
    ],
    notes: [
      "La portata del reset dipende dalla tua implementazione (stato, esiti, assegnazioni)."
    ]
  },

  canDelete: {
    label: "Elimina appuntamento",
    summary: "Consente di eliminare l’evento (soft-delete o hard-delete a seconda del BE).",
    enables: [
      "Bottone 'Elimina' nella modale evento",
      "Rimozione dell’appuntamento dalla agenda/elenco"
    ],
    blocks: [
      "Nessuna possibilità di eliminare appuntamenti da UI"
    ],
    ui: [
      "Icona/cip 'Cestino' o 'Elimina' nella modale",
      "Dopo delete l'evento sparisce dalla visualizzazione"
    ],
    api: [
      "POST /preventivi/agenda/api/delete_event.php",
      "Oppure update_event.php con soft-delete (deleted_at)"
    ],
    notes: [
      "Consigliato usare soft-delete con colonna deleted_at, per poter recuperare.",
      "Da tenere solo a ruoli molto fidati (admin / responsabile)."
    ]
  },

  canToggleSeniorWant: {
    label: "Passa al Senior",
    summary: "Marca o smarca la richiesta di passaggio al Senior (want_senior).",
    enables: [
      "Toggle 'Vuole Senior / Passa al Senior' nella UI",
      "Influenza colori/icone 'hot' in Agenda"
    ],
    blocks: [
      "Il ruolo non può segnare 'Passa al Senior'",
      "Resta solo la visualizzazione dello stato"
    ],
    ui: [
      "Chip/pulsante 'Passa al Senior'",
      "Badge/colore EV-HOT in AgendaPro"
    ],
    api: [
      "POST /preventivi/agenda/api/update_event.php (want_senior=1/0)"
    ],
    notes: [
      "Usato per la pipeline 'Passa al Senior' e per la sezione gestione chiamate."
    ]
  },

  canMarkRilNonBuono: {
    label: "Rilievo non buono",
    summary: "Permette di marcare l’esito del rilievo come 'non buono' (KO tecnico/rilievo).",
    enables: [
      "Toggle 'Rilievo non buono' nella UI",
      "Classificazione degli appuntamenti KO in reportistica"
    ],
    blocks: [
      "Impossibile segnare RIL_KO dal frontend"
    ],
    ui: [
      "Badge rosso / stato 'RIL non buono' in AgendaPro",
      "Filtri/report 'rilievo KO' funzionanti"
    ],
    api: [
      "POST /preventivi/agenda/api/update_event.php (ril_non_buono=1/0)"
    ],
    notes: [
      "Utile per capire dove il cantiere non era idoneo / cliente incompatibile."
    ]
  },

  canSetSeniorEsito: {
    label: "Esito Senior (chiuso / non chiuso)",
    summary: "Gestisce l’esito commerciale del Senior su un appuntamento.",
    enables: [
      "Impostare 'Chiuso' (contratto firmato) o 'Non chiuso'",
      "Colorare gli eventi con colori esito Senior (verde/rosso)",
      "Aggiornare importi 'senior_importo_eur' in modale"
    ],
    blocks: [
      "Ruolo non può modificare l’esito Senior",
      "Esiti solo visibili (se presenti) ma non editabili"
    ],
    ui: [
      "Chip 'Chiuso / Non chiuso' nelle card e nella modale",
      "Colori S6 (chiuso) / S7 (non chiuso) in AgendaPro"
    ],
    api: [
      "POST /preventivi/agenda/api/update_event.php (senior_esito, senior_esito_at, senior_importo_eur)"
    ],
    notes: [
      "Ha priorità sulla colorazione evento se ui_state_code non è valorizzato.",
      "Di solito riservato a senior / responsabile / admin."
    ]
  },

  canUsePreventivatore: {
    label: "Apri Preventivatore",
    summary: "Permette di aprire il preventivatore (Nuovo/Modifica) dall’Agenda tramite link firmato.",
    enables: [
      "Bottone 'Apri Prev.' nella modale AgendaPro",
      "Apertura del preventivatore con cliente, comune, zona già precompilati"
    ],
    blocks: [
      "Nessun link preventivatore dalla modale evento",
      "L’utente deve aprire il preventivatore manualmente (fuori flusso agenda)"
    ],
    ui: [
      "Pulsante 'Apri preventivatore' nella modale AgendaPro",
      "Icona/collegamento verso /preventivi/…"
    ],
    api: [
      "GET /preventivi/agenda/api/link_preventivatore.php (restituisce URL firmato)",
      "Preventivatore su /preventivi/nuovo?token=…"
    ],
    notes: [
      "Hai già il flusso tokenizzato (JWT o simile); questo cap abilita solo l’UI.",
      "Ottimo per ridurre errori di ri-digitazione dati cliente."
    ]
  },

  canUploadFiles: {
    label: "Upload allegati (PDF/Audio/Foto)",
    summary: "Consente di caricare file legati all’appuntamento (audio consultivo, PDF, foto cantiere).",
    enables: [
      "Bottoni 'Carica PDF', 'Carica Audio', 'Carica Foto' attivi",
      "Salvataggio URL file in pdf_path / audio_path / foto_path (se presente)"
    ],
    blocks: [
      "Bottoni di upload nascosti/disabilitati",
      "Impossibile aggiungere nuovi allegati da UI"
    ],
    ui: [
      "Sezione 'Allegati' attiva in modale Agenda/Posa",
      "Link 'Apri PDF', 'Ascolta audio' visibili dopo upload"
    ],
    api: [
      "POST /preventivi/agenda/api/upload_file.php (se presente)",
      "POST /preventivi/agenda_posa/api/upload_file.php"
    ],
    notes: [
      "I percorsi vengono salvati nei campi agenda.audio_path / pdf_path / …",
      "Utile soprattutto per R012 e report di posa."
    ]
  },

  canEditBudgets: {
    label: "Modifica budget/target/real",
    summary: "Permette di modificare i valori economici nella modale (budget cliente, target, valore reale).",
    enables: [
      "Edit dei campi Budget cliente, Target, Real",
      "Aggiornamento dei campi prevenivo_target_eur / pv_total / senior_importo_eur (a seconda della sezione)"
    ],
    blocks: [
      "Campi importo disabilitati",
      "Il ruolo vede solo i valori ma non può modificarli"
    ],
    ui: [
      "Box importi in modale Agenda/R012/R012 Posa editabili"
    ],
    api: [
      "POST /preventivi/agenda/api/update_event.php (campi budget/real)",
      "POST /preventivi/agenda_posa/api/updateP_event.php (campi contratto_eur, ecc.)"
    ],
    notes: [
      "Può essere tenuto a ruoli di controllo (responsabile, formatore) per correzioni manuali."
    ]
  },


  "gestione.access": {
    label: "Gestione: accesso pagina",
    summary: "Consente di vedere la voce 'Gestione' in navbar e di aprire la pagina /preventivi/gestione.",
    enables: [
      "Voce 'Gestione' visibile nella navbar",
      "Accesso alla rotta /preventivi/gestione (guard FE abilita la pagina)"
    ],
    blocks: [
      "La voce 'Gestione' non è visibile",
      "La rotta mostra 'Permesso negato' (guard FE)"
    ],
    ui: [
      "Navbar: bottone 'Gestione'",
      "Pagina 'Gestione' (4 tab) disponibile"
    ],
    api: [
      // Nessuna API specifica: è un permesso di accesso UI
    ],
    notes: [
      "Se vuoi che anche gli admin siano governati dalla Matrix, rimuovi il bypass admin nel NavBar e nel guard."
    ]
  },


  /* ================== Chat ================== */

  "chat.use": {
    label: "Chat interna",
    summary: "Permette di usare il pulsante Chat nella navbar e inviare/ricevere messaggi interni in tempo quasi reale.",
    enables: [
      "Mostra il pulsante 'Chat' nella barra in alto",
      "Permette di aprire il pannellino di messaggistica in basso a destra",
      "Inviare messaggi diretti ad altri utenti (rilevatori, senior, responsabili, ecc.)",
      "Vedere lo storico messaggi con il destinatario selezionato"
    ],
    blocks: [
      "Il pulsante 'Chat' non è visibile (se non sei admin)",
      "Non è possibile inviare o leggere messaggi interni dall'app"
    ],
    ui: [
      "Navbar: bottone 'Chat' (💬)",
      "Pannello chat con selezione destinatario e lista messaggi"
    ],
    api: [
      "POST /preventivi/chat/list_users.php",
      "POST /preventivi/chat/history.php",
      "POST /preventivi/chat/send_message.php",
      "POST /preventivi/chat/ping.php"
    ],
    notes: [
      "Gli admin vedono sempre la Chat a prescindere, per debug.",
      "Per gli altri ruoli, la visibilità è governata da questo permesso."
    ]
  },




  
  /* ================== SICUREZZA / VISIBILITÀ ================== */
  phoneDelay: {
    label: "Oscura telefono (−20 min)",
    summary: "Oscura il telefono cliente fino a 20 minuti prima dell’orario appuntamento (per ruoli selezionati).",
    enables: [
      "Numero cliente visibile solo entro la soglia temporale (es. da −20')",
      "Nel resto del tempo appare mascherato (es. 333 ••• ••45)"
    ],
    blocks: [
      "Se il cap è OFF: telefono sempre visibile ai ruoli con clienti.read"
    ],
    ui: [
      "Tooltip e modale mostrano '••••••••' fino alla soglia",
      "Badge o icona che indica la protezione attiva"
    ],
    api: [
      "È gestito lato FE (no API dedicata), ma può usare l’orario 'start' dell’evento"
    ],
    notes: [
      "Tipico per ruoli esterni (rilevatore) per non esporre il numero troppo tempo prima.",
      "Admin / centralino di solito NON hanno phoneDelay."
    ]
  },

  readOnly: {
    label: "Sola lettura",
    summary: "Forza il ruolo in modalità sola lettura: niente azioni di modifica anche se altri permessi sono presenti.",
    enables: [
      "— (è un vincolo, non un 'enable')"
    ],
    blocks: [
      "Drag/resize eventi",
      "Assegnazioni Rilevatore/Senior",
      "Conferme/annullamenti",
      "Modifica esiti senior/posa",
      "Upload allegati",
      "Eliminazione eventi"
    ],
    ui: [
      "Tutti i controlli di azione disabilitati",
      "Cursori 'not-allowed' su operazioni vietate"
    ],
    api: [
      "FE non invia POST/DELETE quando readOnly attivo (anche se altre cap sarebbero true)"
    ],
    notes: [
      "Ha precedenza logica sugli altri permessi di azione.",
      "Utile per ruoli 'ospite' o sessioni di training."
    ]
  },

  restrictAssignedOnly: {
    label: "Mostra solo appuntamenti assegnati a me",
    summary: "Limita la visibilità agli appuntamenti assegnati al proprio utente (o alla propria squadra).",
    enables: [
      "Filtri aggiuntivi su list_events per 'solo assegnati'",
      "Agenda filtrata per Rilevatore/Senior loggato"
    ],
    blocks: [
      "Se OFF: il ruolo può vedere anche eventi di altri (a seconda di altri filtri)"
    ],
    ui: [
      "Badge / toggle 'Solo assegnati' attivo",
      "Griglia che si riduce agli appuntamenti del proprio utente"
    ],
    api: [
      "GET /preventivi/agenda/api/list_events.php?assigned_to=UID (o filtro equivalente)"
    ],
    notes: [
      "Accoppiato con zone, impedisce a un rilevatore di vedere appuntamenti di altri colleghi."
    ]
  },

  /* ============== PREVENTIVI / REPORT / ALTRO ============== */
  "preventivi.read": {
    label: "Preventivi: lettura",
    summary: "Permette di visualizzare i preventivi collegati (PDF, schede) agli appuntamenti.",
    enables: [
      "Link 'PDF preventivo' attivo nelle modali/elenchi",
      "Apertura delle schede preventivo in read-only"
    ],
    blocks: [
      "Nessun link ai PDF/alle schede preventivo",
      "L’utente non può vedere il contenuto del preventivo"
    ],
    ui: [
      "Icona PDF / pulsante 'Apri preventivo' visibile"
    ],
    api: [
      "GET /preventivi/generapdf.php?id=…",
      "GET /preventivi/api/quote_get.php"
    ],
    notes: []
  },

  "preventivi.write": {
    label: "Preventivi: scrittura",
    summary: "Consente di creare, modificare e duplicare i preventivi.",
    enables: [
      "Pulsanti 'Nuovo preventivo', 'Modifica', 'Duplica' attivi",
      "Salvataggio delle modifiche nella pagina preventivo"
    ],
    blocks: [
      "Preventivi solo in lettura (niente 'Salva' / 'Duplica')"
    ],
    ui: [
      "Sezione preventivi con form modificabili",
      "Bottoni 'Salva', 'Duplica' attivi"
    ],
    api: [
      "POST /preventivi/api/quote_save.php"
    ],
    notes: [
      "Per il ruolo 'ospite' è consigliato tenere questo a NO (read-only)."
    ]
  },

  "report.read": {
    label: "Report: lettura",
    summary: "Consente di aprire i report (R012, statistiche, cruscotti) legati ad agenda/preventivi.",
    enables: [
      "Accesso alla pagina Statistiche",
      "Apertura R012 dal link in modale AgendaPro",
      "Vista dei cruscotti KPI (zone, rilevatori, senior, ecc.)"
    ],
    blocks: [
      "Nessun accesso ai report statistici",
      "Link R012/Report disabilitati"
    ],
    ui: [
      "Voce 'Statistiche' in navbar",
      "Pulsante/link R012 nella modale AgendaPro"
    ],
    api: [
      "GET /preventivi/report/r012.php?event_id=…",
      "GET /preventivi/report/statistiche.php?…"
    ],
    notes: []
  },

  "reset.state": {
    label: "Reset stato (hard)",
    summary: "Esegue un reset ancora più profondo dello stato (rispetto a canReset), con logica definita dal BE.",
    enables: [
      "Pulsante 'Reset (hard)' (se la UI lo espone)",
      "Pulizia di campi aggiuntivi (es. esiti, lock interni, flag posa/resell) secondo regole BE"
    ],
    blocks: [
      "Disponibile solo il reset 'soft' (canReset)",
      "Per interventi drastici si deve agire da BE/DB"
    ],
    ui: [
      "Eventuale bottone 'Reset hard' o simile in modale"
    ],
    api: [
      "POST /preventivi/agenda/api/reset_event.php?hard=1 (se implementato)"
    ],
    notes: [
      "Usare con cautela: di solito solo per admin o sviluppatori."
    ]
  }
};


// Helpers meta
const getCapMeta = (cap) => CAP_META[cap?.code || cap] || null;
const capTitle   = (cap) => cap?.label || getCapMeta(cap)?.label || cap?.code || "Permesso";
const capSummary = (cap) => getCapMeta(cap)?.summary || "—";
const capShort   = (c) =>
  String(c.short || c.label || c.code)
    .replace(/Rilevatore/gi, "Ril.")
    .replace(/Senior/gi, "Sr.")
    .replace(/Conferma/gi, "Conf.")
    .replace(/Assegna/gi, "Ass.")
    .replace(/Appuntamento/gi, "App.")
    .replace(/Preventivatore/gi, "Prev.");

/* ==================== Tip pannello ==================== */
function TipPanel({ cap }) {
  const meta = CAP_META[cap?.code] || {};
  const Section = ({ label, items }) =>
    Array.isArray(items) && items.length ? (
      <div className="mx-tip-section">
        <div className="mx-tip-section-title">{label}</div>
        <ul className="mx-tip-list">{items.map((t,i)=><li key={i}>{t}</li>)}</ul>
      </div>
    ) : null;
  if (!cap) {
    return (
      <div className="mx-tip-panel">
        <div className="mx-tip-title">Seleziona un permesso</div>
        <div className="mx-tip-summary">A sinistra scegli un permesso (o un ruolo) per vedere i dettagli qui.</div>
      </div>
    );
  }
  return (
    <div className="mx-tip-panel">
      <div className="mx-tip-head">
        <div className="mx-tip-title">{capTitle(cap)}</div>
        <span className="mx-submuted">{cap.code}</span>
      </div>
      <div className="mx-tip-summary">{capSummary(cap)}</div>
      <Section label="Abilita" items={meta.enables} />
      <Section label="Blocca quando è NO" items={meta.blocks} />
      <Section label="Impatto UI" items={meta.ui} />
      <Section label="Back-end / API" items={meta.api} />
      <Section label="Note" items={meta.notes} />
    </div>
  );
}

/* ==================== COMPONENTE PRINCIPALE ==================== */
export default function AccessMatrix() {
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const [roles, setRoles]   = useState([]);
  const [caps, setCaps]     = useState([]);
  const [matrix, setMatrix] = useState({});

  const [zones, setZones] = useState([]);
  const [zoneId, setZoneId] = useState("");

  const [roleQuery, setRoleQuery] = useState("");
  const [capQuery,  setCapQuery]  = useState("");

  const [view, setView] = useState("perm"); // "perm" | "role"
  const [activeCap, setActiveCap] = useState(null);
  const [activeRoleId, setActiveRoleId] = useState(null);

  const [savedAt, setSavedAt] = useState(0);

  /* ========================= LOAD ========================= */
  const load = useCallback(async () => {
    setLoading(true); setErr("");
    try {
      const q = zoneId ? `?${ZONE_PARAM}=${encodeURIComponent(zoneId)}` : "";
      const j = await fetchJSON(`${API_BASE}/perm_list.php${q}`);

      const normRolesRaw = Array.isArray(j.roles) ? j.roles.slice() : [];
      const normRoles = normRolesRaw.map((r) => {
        const id      = Number(r.id);
        const rawSlug = String((r.slug ?? r.code) ?? "").trim();
        const rawLbl  = String(r.label ?? "").trim();
        const mkey    = rawSlug || String(r.code ?? "") || String(id);
        const useNum  = /^\d+$/.test(rawSlug || "");
        const uiSlug  = (!rawSlug || useNum) ? (ROLE_FALLBACK[id]?.slug || `role_${id}`) : rawSlug;
        const lblFromSlug = uiSlug.replace(/_/g, " ").replace(/\b\w/g, (m) => m.toUpperCase());
        const uiLabel = (!rawLbl || /^\d+$/.test(rawLbl))
          ? (ROLE_FALLBACK[id]?.label || lblFromSlug)
          : rawLbl;
        return { ...r, id, _mkey: mkey, slug: uiSlug, code: uiSlug, label: uiLabel, is_active: r.is_active ?? 1 };
      });
      normRoles.sort((a, b) => String(a.label || "").localeCompare(String(b.label || "")));

      const normCaps = Array.isArray(j.caps) ? j.caps.slice() : [];
      normCaps.sort((a, b) => String(a.label || a.code || "").localeCompare(String(b.label || b.code || "")));

      const byId = {};
      const m = j.matrix || {};
      for (const r of normRoles) {
        const codeKey = r._mkey;
        const rid     = r.id;
        const row     = (m && codeKey && m[codeKey]) ? m[codeKey] : {};
        const out     = {};
        for (const c of normCaps) out[c.code] = row[c.code] ? 1 : 0;
        byId[rid] = out;
      }

      setRoles(normRoles);
      setCaps(normCaps);
      setMatrix(byId);
      setZones(Array.isArray(j.zones) ? j.zones : []);

      // selezioni iniziali
      if (!activeCap && normCaps.length) setActiveCap(normCaps[0]);
      if (!activeRoleId && normRoles.length) setActiveRoleId(normRoles[0].id);
    } catch (e) {
      setErr(e.message || "Errore caricamento");
    } finally {
      setLoading(false);
    }
  }, [zoneId, activeCap, activeRoleId]);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [load]);

  /* ========================= SAVE (debounced) ========================= */
  const DEBOUNCE_MS = 300;
  const saveQueueRef = useRef(new Map());

  useEffect(() => {
    return () => {
      const q = saveQueueRef.current;
      q.forEach(({ timer, controller }) => {
        if (timer) clearTimeout(timer);
        if (controller) controller.abort();
      });
      q.clear();
    };
  }, []);

  const postPerm = async (body, signal) => {
    return fetchJSON(`${API_BASE}/perm_save.php`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
      signal
    });
  };

  const scheduleSave = useCallback((rid, code, allowed) => {
    const key = `${zoneId || "0"}|${rid}|${code}`;
    const q = saveQueueRef.current;

    const prev = q.get(key);
    if (prev?.timer) clearTimeout(prev.timer);
    if (prev?.controller) prev.controller.abort();

    const timer = setTimeout(async () => {
      const controller = new AbortController();
      q.set(key, { controller });

      try {
        const role = roles.find(r => r.id === rid);
        const roleCode = role?._mkey || role?.slug || role?.code || null;

        const body = {
          role_id: Number(rid),
          perm: code,
          value: allowed ? "YES" : "NO",
          cap_code: code,
          allow: allowed ? 1 : 0,
          ...(roleCode ? { role: roleCode, role_code: roleCode } : {}),
          ...(zoneId ? { [ZONE_PARAM]: Number(zoneId), zone_id: Number(zoneId) } : {})
        };
        await postPerm(body, controller.signal);
        setSavedAt(Date.now());
      } catch (e) {
        if (e?.name !== "AbortError") alert(e?.message || "Errore salvataggio");
      } finally {
        q.delete(key);
      }
    }, DEBOUNCE_MS);

    q.set(key, { timer });
  }, [zoneId, roles]);

  const setCell = useCallback((rid, code, boolVal) => {
    setMatrix(prev => {
      const row = { ...(prev?.[rid] || {}) };
      row[code] = boolVal ? 1 : 0;
      return { ...prev, [rid]: row };
    });
    scheduleSave(rid, code, !!boolVal);
  }, [scheduleSave]);

  // bulk role → tutti i permessi visibili
  const setRowVisible = (rid, val, codes = null) => {
    const list = codes || caps.map(c => c.code);
    const patch = list.reduce((acc, code) => ((acc[code] = val ? 1 : 0), acc), {});
    setMatrix(prev => ({ ...prev, [rid]: { ...(prev[rid] || {}), ...patch } }));
    saveBulk({ [rid]: patch });
  };

  // bulk perm → tutti i ruoli visibili
  const setColVisible = (code, val, roleIds = null) => {
    const ids = roleIds || roles.map(r => r.id);
    const patch = {}; ids.forEach(id => (patch[id] = { [code]: val ? 1 : 0 }));
    setMatrix(prev => {
      const next = { ...prev };
      ids.forEach(id => (next[id] = { ...(next[id] || {}), [code]: val ? 1 : 0 }));
      return next;
    });
    saveBulk(patch);
  };

  const buildChanges = (partialMatrix) => {
    const changes = [];
    for (const [rid, capsObj] of Object.entries(partialMatrix || {})) {
      const role = roles.find(r => r.id === Number(rid));
      const roleCode = role?._mkey || role?.slug || role?.code || null;
      for (const [code, on] of Object.entries(capsObj)) {
        changes.push({
          ...(roleCode ? { role: roleCode } : {}),
          role_id: Number(rid),
          perm: code,
          value: on ? "YES" : "NO",
          ...(zoneId ? { [ZONE_PARAM]: Number(zoneId) } : {})
        });
      }
    }
    return changes;
  };
  const saveBulk = async (partialMatrix) => {
    try {
      const changes = buildChanges(partialMatrix);
      if (!changes.length) return;
      await fetchJSON(`${API_BASE}/perm_save.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ changes }),
      });
      setSavedAt(Date.now());
    } catch (e) {
      alert(e.message || "Errore salvataggio");
    }
  };

  /* ========================= Filtri/visibilità ========================= */
  const visRoles = useMemo(() => {
    const q = roleQuery.trim().toLowerCase();
    if (!q) return roles;
    return roles.filter(r =>
      String(r.label || "").toLowerCase().includes(q) ||
      String(r.slug  || r.code || "").toLowerCase().includes(q)
    );
  }, [roles, roleQuery]);

  const visCaps = useMemo(() => {
    const q = capQuery.trim().toLowerCase();
    if (!q) return caps;
    return caps.filter(c =>
      String(c.label || "").toLowerCase().includes(q) ||
      String(c.code  || "").toLowerCase().includes(q)
    );
  }, [caps, capQuery]);

  // Cap selezionato sempre coerente con lista filtrata
  useEffect(() => {
    if (!activeCap || !visCaps.some(c => c.code === activeCap.code)) {
      setActiveCap(visCaps[0] || null);
    }
  }, [visCaps, activeCap]);

  // Role selezionato sempre coerente con lista filtrata
  useEffect(() => {
    if (activeRoleId == null || !visRoles.some(r => r.id === activeRoleId)) {
      setActiveRoleId(visRoles[0]?.id ?? null);
    }
  }, [visRoles, activeRoleId]);

  /* ========================= Utilità ========================= */
  const capsByGroup = useMemo(() => {
    const map = new Map();
    visCaps.forEach(c => {
      const g = capGroup(c.code);
      if (!map.has(g)) map.set(g, []);
      map.get(g).push(c);
    });
    return map;
  }, [visCaps]);

  const rolesById = useMemo(
    () => Object.fromEntries(roles.map(r => [r.id, r])),
    [roles]
  );

  const colOnCount = useCallback((code, list = visRoles) => {
    return list.reduce((acc, r) => acc + ((matrix[r.id] || {})[code] ? 1 : 0), 0);
  }, [visRoles, matrix]);

  const rowOnCount = useCallback((rid) => {
    const row = matrix[rid] || {};
    return visCaps.reduce((acc, c) => acc + (row[c.code] ? 1 : 0), 0);
  }, [matrix, visCaps]);

  /* ========================= Snackbar ========================= */
  useEffect(() => {
    if (!savedAt) return;
    const t = setTimeout(() => setSavedAt(0), 1200);
    return () => clearTimeout(t);
  }, [savedAt]);

  /* ========================= RENDER ========================= */
  return (
    <div className="mx-page">
      {loading || err ? (
        <div className="mx-card" style={{ padding: 14 }}>
          {loading && <div>Carico…</div>}
          {err && (
            <div style={{ color:"#b00", marginTop:8 }}>
              <div style={{marginBottom:8}}>Errore: {err}</div>
              <button type="button" className="chip" onClick={() => load()}>
                Riprova
              </button>
            </div>
          )}
        </div>
      ) : (
        <div className="mx-card">
          {/* Toolbar */}
          <div className="mx-toolbar">
            <button
              type="button"
              className={`chip ${view==="perm"?"on":""}`}
              onClick={() => setView("perm")}
              title="Gestisci per Permesso"
            >
              Per Permesso
            </button>
            <button
              type="button"
              className={`chip ${view==="role"?"on":""}`}
              onClick={() => setView("role")}
              title="Gestisci per Ruolo"
            >
              Per Ruolo
            </button>

            {view==="perm" ? (
              <input value={capQuery} onChange={e=>setCapQuery(e.target.value)} placeholder="Filtra permessi…" className="chip" />
            ) : (
              <input value={roleQuery} onChange={e=>setRoleQuery(e.target.value)} placeholder="Filtra ruoli…" className="chip" />
            )}

            {(capQuery || roleQuery) && (
              <button type="button" className="chip" onClick={() => { setRoleQuery(""); setCapQuery(""); }}>
                Pulisci filtri
              </button>
            )}

            <span style={{ marginLeft:"auto", fontSize:12, opacity:.8 }}>
              Permessi visibili: <b>{visCaps.length}</b>/<b>{caps.length}</b>
            </span>

            {Array.isArray(zones) && zones.length>0 && (
              <>
                <span style={{ marginLeft:8, fontSize:12, opacity:.8 }}>Zona:</span>
                <select
                  value={zoneId}
                  onChange={(e) => { const v = e.target.value; setZoneId(v); try { localStorage.setItem("admin_perm_zone", v || ""); } catch {} }}
                  className="chip"
                >
                  <option value="">(Globale / tutte le zone)</option>
                  {zones.map(z => (
                    <option key={z.id} value={String(z.id)}>
                      {z.label || z.nome || z.slug || `Zona #${z.id}`}
                    </option>
                  ))}
                </select>
              </>
            )}
          </div>

          <div className="mx-legend">
            <span>Legenda:</span>
            <span className="dot yes" /> <span>SI</span>
            <span className="dot no" /> <span>NO</span>
            <span className="dot muted" /> <span>N/D</span>
          </div>

          {/* ======== LAYOUT 2‑PANE ======== */}
          <div className="mx-layout">
            {/* LATO SINISTRO: lista (perm/ruolo) */}
            <aside className="mx-side">
              {view==="perm" ? (
                <div className="mx-list">
                  {[...capsByGroup.entries()].map(([g, arr]) => (
                    <div key={g}>
                      <div className="mx-group-label">{g}</div>
                      {arr.map(c => {
                        const on = colOnCount(c.code, visRoles);
                        const ratio = on / Math.max(1, visRoles.length);
                        const active = activeCap && activeCap.code === c.code;
                        return (
                          <div key={c.code} className={`mx-item ${active ? "active":""}`} onClick={() => setActiveCap(c)}>
                            <div className="mx-flex-col">
                              <div className="mx-item-title">{capTitle(c)}</div>
                              <div className="mx-item-sub">{c.code}</div>
                            </div>
                            <div className="mx-meter" title={`${on}/${visRoles.length}`}>
                              <i style={{ width: `${ratio*100}%` }} />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="mx-list">
                  <div className="mx-group-label">Ruoli</div>
                  {visRoles.map(r => {
                    const active = r.id === activeRoleId;
                    const count = rowOnCount(r.id);
                    return (
                      <div key={r.id} className={`mx-item ${active ? "active":""}`} onClick={() => setActiveRoleId(r.id)}>
                        <div className="mx-flex-col">
                          <div className="mx-item-title">{r.label}</div>
                          <div className="mx-item-sub">{r.slug || r.code}</div>
                        </div>
                        <div className="mx-item-sub">{count}/{visCaps.length}</div>
                      </div>
                    );
                  })}
                </div>
              )}
            </aside>

            {/* LATO DESTRO: contenuto */}
            <main className="mx-main">
              {view==="perm" ? (
                activeCap && (
                  <div className="mx-perm-main">
                    {/* Colonna ruoli per il permesso */}
                    <section className="mx-perm-roles">
                      <div className="mx-subbar">
                        <div className="mx-subtitle">
                          {capTitle(activeCap)} <span className="mx-submuted">({capGroup(activeCap.code)})</span>
                        </div>
                        <div className="mx-submuted">
                          Attivo: <b>{colOnCount(activeCap.code, visRoles)}</b>/<b>{visRoles.length}</b>
                        </div>
                        <button type="button" className="chip" onClick={() => setColVisible(activeCap.code, 1)}>✓ Tutti</button>
                        <button type="button" className="chip" onClick={() => setColVisible(activeCap.code, 0)}>✕ Nessuno</button>
                      </div>

                      <div className="mx-role-list">
                        {visRoles.map(r => {
                          const val = (matrix[r.id] || {})[activeCap.code] ? 1 : 0;
                          const inactive = String(r.is_active) === "0";
                          return (
                            <div key={r.id} className="mx-row">
                              <div className="mx-r-label">
                                <div className="mx-r-title">{r.label}</div>
                                <div className="mx-r-sub">{r.slug || r.code}</div>
                              </div>
                              <button
                                type="button"
                                className={`mx-pill ${val ? "si" : "no"}`}
                                disabled={inactive}
                                onClick={() => setCell(r.id, activeCap.code, !val)}
                                title={`${r.label} → ${capTitle(activeCap)}: ${val ? "SI" : "NO"}`}
                              >
                                {val ? "SI" : "NO"}
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    </section>

                    {/* Pannello descrizioni fisso (350px) */}
                    <aside className="mx-perm-info">
                      <TipPanel cap={activeCap} />
                    </aside>
                  </div>
                )
              ) : (
                activeRoleId != null && (
                  <div className="mx-role-main">
                    {/* Colonna permessi del ruolo */}
                    <section className="mx-role-perms">
                      {(() => {
                        const r = rolesById[activeRoleId];
                        if (!r) return null;
                        const inactive = String(r.is_active) === "0";
                        return (
                          <>
                            <div className="mx-subbar">
                              <div className="mx-subtitle">{r.label}</div>
                              <div className="mx-submuted">{r.slug || r.code}</div>
                              <div className="mx-submuted">
                                Attivi: <b>{rowOnCount(r.id)}</b>/<b>{visCaps.length}</b>
                              </div>
                              <button type="button" className="chip" disabled={inactive} onClick={() => setRowVisible(r.id, 1)}>
                                ✓ Tutto
                              </button>
                              <button type="button" className="chip" disabled={inactive} onClick={() => setRowVisible(r.id, 0)}>
                                ✕ Niente
                              </button>
                            </div>

                            {[...capsByGroup.entries()].map(([g, arr]) => {
                              const codes = arr.map(c => c.code);
                              return (
                                <div key={g} className="mx-group">
                                  <div className="mx-group-head">
                                    <span>{g}</span>
                                    <span style={{display:"inline-flex", gap:6}}>
                                      <button type="button" className="chip" disabled={inactive} onClick={() => setRowVisible(r.id, 1, codes)}>✓ gruppo</button>
                                      <button type="button" className="chip" disabled={inactive} onClick={() => setRowVisible(r.id, 0, codes)}>✕ gruppo</button>
                                    </span>
                                  </div>
                                  {arr.map(c => {
                                    const val = (matrix[r.id] || {})[c.code] ? 1 : 0;
                                    return (
                                      <div key={c.code} className="mx-cap-row">
                                        <div style={{minWidth:0}}>
                                          <div className="mx-item-title" title={capTitle(c)}>{capShort(c)}</div>
                                          <div className="mx-item-sub">{c.code}</div>
                                        </div>
                                        <div style={{display:"flex", alignItems:"center", gap:6}}>
                                          <button
                                            type="button"
                                            className="chip"
                                            title="Dettagli permesso"
                                            onClick={() => setActiveCap(c)}
                                          >
                                            ?
                                          </button>
                                          <button
                                            type="button"
                                            className={`mx-pill ${val ? "si" : "no"}`}
                                            disabled={inactive}
                                            onClick={() => setCell(r.id, c.code, !val)}
                                            title={`${r.label} → ${capTitle(c)}: ${val ? "SI" : "NO"}`}
                                          >
                                            {val ? "SI" : "NO"}
                                          </button>
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              );
                            })}
                          </>
                        );
                      })()}
                    </section>

                    {/* Pannello descrizioni fisso (350px) */}
                    <aside className="mx-perm-info">
                      <TipPanel cap={activeCap} />
                    </aside>
                  </div>
                )
              )}
            </main>
          </div>

          {/* Snackbar “Salvato” */}
          {savedAt > 0 && <div className="mx-snack" role="status" aria-live="polite">Salvato</div>}
        </div>
      )}
    </div>
  );
}
